/**
 * SA 請求書 公開ページ (v3.2.14 / Phase 3a)。
 *
 * URL: /c/sa-invoice/:token
 *
 * 経理担当が請求書を閲覧する用のページ。トークン照合で 1 件取得。
 * 認証は Firebase Auth のみ (Firestore Rules で recipientId == auth.uid または editor+ )。
 * PDF ダウンロードは window.print() でブラウザの印刷 → PDF 保存に依存。
 */
import type { CommissionInvoice } from '@goenhub/shared';
import { COMMISSION_INVOICE_STATUS_LABELS } from '@goenhub/shared';
import { Download, FileText, Printer } from 'lucide-react';
import { useParams } from 'react-router-dom';

import { Spinner } from '@/components/ui/spinner';
import { useCommissionInvoiceByToken } from '@/hooks/useCommissionInvoices';
import { usePublicPageMode } from '@/hooks/usePublicPageMode';

function fmtDate(v: unknown): string {
  if (!v) return '-';
  if (typeof v === 'string') return new Date(v).toLocaleDateString('ja-JP');
  if (typeof v === 'object' && v !== null && 'toDate' in v) {
    return (v as { toDate: () => Date }).toDate().toLocaleDateString('ja-JP');
  }
  return '-';
}

function accountTypeLabel(t: CommissionInvoice['bankAccountSnapshot']) {
  if (!t) return '';
  return t.accountType === 'ordinary' ? '普通' : t.accountType === 'checking' ? '当座' : '貯蓄';
}

export function CommissionInvoicePublicPage() {
  const { token } = useParams<{ token: string }>();
  const invoice = useCommissionInvoiceByToken(token);

  // globals.css の body { overflow:hidden; height:100vh } は AppLayout 専用。
  // AppLayout 外のこのページでは解除しないと縦スクロールできない。
  usePublicPageMode();

  if (invoice.isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner />
      </div>
    );
  }
  if (!invoice.data) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="text-center space-y-3">
          <FileText className="size-12 mx-auto text-[var(--text-muted)] opacity-50" />
          <h1 className="text-lg font-semibold">請求書が見つかりません</h1>
          <p className="text-sm text-[var(--text-muted)]">
            URL が正しくないか、リンクが無効化されています。
          </p>
        </div>
      </div>
    );
  }

  const inv = invoice.data;
  const handlePrint = () => window.print();

  return (
    <div className="min-h-screen print:min-h-0 bg-[var(--bg-elevated)] py-8 print:py-0 print:bg-white">
      <style>{`
        @media print {
          /* 100vh 固定・overflow:hidden のアプリ用レイアウトを印刷時は解除。
             残しておくと内容が切れたり空白ページが生まれる。 */
          html, body, #root {
            height: auto !important;
            min-height: 0 !important;
            overflow: visible !important;
          }
        }
      `}</style>
      {/* ツールバー (印刷時非表示) */}
      <div className="max-w-3xl mx-auto mb-4 print:hidden flex items-center justify-end gap-2 px-6">
        <button
          type="button"
          onClick={handlePrint}
          className="px-3 py-2 text-sm rounded-md border border-[var(--border)] bg-[var(--bg-elevated)] hover:bg-[var(--bg-hover)] inline-flex items-center gap-1.5"
        >
          <Printer className="size-3.5" />
          印刷
        </button>
        <button
          type="button"
          onClick={handlePrint}
          className="px-3 py-2 text-sm rounded-md bg-[var(--accent)] text-white hover:opacity-90 inline-flex items-center gap-1.5"
        >
          <Download className="size-3.5" />
          PDF 保存 (印刷 → PDF)
        </button>
      </div>

      {/* 請求書本体 */}
      <div className="max-w-3xl mx-auto bg-white text-black p-10 shadow-lg print:shadow-none print:max-w-none print:p-6">
        <header className="text-center pb-6 border-b-2 border-black">
          <h1 className="text-3xl font-bold tracking-widest">請求書</h1>
          <p className="mt-2 text-sm text-gray-600">Invoice</p>
        </header>

        <section className="mt-6 grid grid-cols-2 gap-6">
          {/* 宛先 (請求先 = 弊社経理) */}
          <div>
            <div className="text-xs text-gray-500 uppercase tracking-wider">宛先</div>
            <div className="mt-1 text-lg font-semibold">caudesign 経理担当 御中</div>
          </div>
          {/* 請求書情報 */}
          <div className="text-right">
            <div className="text-xs text-gray-500">請求番号</div>
            <div className="font-mono text-base">{inv.invoiceNumber}</div>
            <div className="text-xs text-gray-500 mt-2">発行日</div>
            <div>{fmtDate(inv.issuedAt)}</div>
            {inv.periodLabel && (
              <>
                <div className="text-xs text-gray-500 mt-2">対象期間</div>
                <div>{inv.periodLabel}</div>
              </>
            )}
            <div className="text-xs text-gray-500 mt-2">状態</div>
            <div>{COMMISSION_INVOICE_STATUS_LABELS[inv.status]}</div>
          </div>
        </section>

        {/* 発行元 */}
        <section className="mt-6 pb-4 border-b border-gray-300">
          <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">発行元</div>
          <div className="text-lg font-semibold">{inv.recipientName}</div>
          {inv.recipientEmail && (
            <div className="text-sm text-gray-600">{inv.recipientEmail}</div>
          )}
          {inv.invoiceRegistrationNumber && (
            <div className="text-sm mt-1">
              <span className="text-gray-500">適格請求書発行事業者登録番号:</span>{' '}
              <span className="font-mono">{inv.invoiceRegistrationNumber}</span>
            </div>
          )}
        </section>

        {/* 明細 */}
        <section className="mt-6">
          <h2 className="text-sm font-semibold mb-2">内訳</h2>
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="border-b-2 border-gray-700 text-left">
                <th className="py-2 px-2 w-10">#</th>
                <th className="py-2 px-2">商談 / 顧客</th>
                <th className="py-2 px-2">商品</th>
                <th className="py-2 px-2 w-24 text-right">報酬額 (税抜)</th>
              </tr>
            </thead>
            <tbody>
              {inv.lineItems.map((l, i) => (
                <tr key={l.recordId ?? `line-${i}`} className="border-b border-gray-200">
                  <td className="py-2 px-2 tabular-nums">{i + 1}</td>
                  <td className="py-2 px-2">
                    <div className="font-medium">
                      {l.kind === 'briefing_fee' ? '—' : l.dealTitle}
                    </div>
                    <div className="text-xs text-gray-600">{l.contactName ?? ''}</div>
                  </td>
                  <td className="py-2 px-2">{l.productName}</td>
                  <td className="py-2 px-2 text-right tabular-nums">
                    ¥{l.amount.toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        {/* 合計 */}
        <section className="mt-6 flex justify-end">
          <div className="w-72 space-y-1 text-sm">
            <div className="flex justify-between border-b border-gray-200 py-1">
              <span>小計 (税抜)</span>
              <span className="tabular-nums">¥{inv.subtotalExcludingTax.toLocaleString()}</span>
            </div>
            {inv.tax.rate > 0 ? (
              <div className="flex justify-between border-b border-gray-200 py-1">
                <span>消費税 ({inv.tax.rate}%)</span>
                <span className="tabular-nums">¥{inv.tax.amount.toLocaleString()}</span>
              </div>
            ) : (
              <div className="flex justify-between border-b border-gray-200 py-1 text-gray-500 text-xs">
                <span>※ 免税事業者</span>
                <span>—</span>
              </div>
            )}
            <div className="flex justify-between pt-2 text-lg font-bold border-t-2 border-black">
              <span>合計</span>
              <span className="tabular-nums">¥{inv.totalIncludingTax.toLocaleString()}</span>
            </div>
          </div>
        </section>

        {/* 振込先 */}
        {inv.bankAccountSnapshot && (
          <section className="mt-8 pt-4 border-t border-gray-300">
            <h2 className="text-sm font-semibold mb-2">お振込先</h2>
            <table className="text-sm">
              <tbody>
                <tr>
                  <td className="pr-4 py-0.5 text-gray-500">銀行</td>
                  <td>{inv.bankAccountSnapshot.bankName}</td>
                </tr>
                <tr>
                  <td className="pr-4 py-0.5 text-gray-500">支店</td>
                  <td>{inv.bankAccountSnapshot.branchName}</td>
                </tr>
                <tr>
                  <td className="pr-4 py-0.5 text-gray-500">種別</td>
                  <td>{accountTypeLabel(inv.bankAccountSnapshot)}</td>
                </tr>
                <tr>
                  <td className="pr-4 py-0.5 text-gray-500">口座番号</td>
                  <td className="font-mono">{inv.bankAccountSnapshot.accountNumber}</td>
                </tr>
                <tr>
                  <td className="pr-4 py-0.5 text-gray-500">名義</td>
                  <td>{inv.bankAccountSnapshot.accountHolder}</td>
                </tr>
              </tbody>
            </table>
          </section>
        )}

        {/* メモ */}
        {inv.memo && (
          <section className="mt-6 pt-4 border-t border-gray-300">
            <h2 className="text-sm font-semibold mb-1">備考</h2>
            <p className="text-sm whitespace-pre-wrap">{inv.memo}</p>
          </section>
        )}
      </div>
    </div>
  );
}
